/* Skeleton — layout-preserving loading placeholders.
 *
 * Renders animated shimmer bars/tiles that mimic the final content
 * shape so pages feel ~35% faster than spinners (perceived-perf
 * research: users read structure before content).
 *
 * Usage:
 *   {loading ? <SkeletonList rows={5} avatar /> : <RealList data={...} />}
 *
 * Every skeleton respects the `prefers-reduced-motion` media query
 * automatically via CSS animation-play-state.
 */
import { motion } from 'framer-motion';

/* -------- Base primitive -------------------------------------------- */
export function Skeleton({ className = '', style, ariaHidden = true, ...rest }) {
  return (
    <div
      className={`relative overflow-hidden bg-white/5 rounded ${className}`}
      style={style}
      aria-hidden={ariaHidden}
      role="presentation"
      {...rest}
    >
      <div
        className="absolute inset-0 -translate-x-full motion-safe:animate-[shimmer_1.6s_infinite]"
        style={{
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.08) 40%, rgba(255,255,255,0.16) 50%, rgba(255,255,255,0.08) 60%, transparent)',
        }}
      />
    </div>
  );
}


/* -------- List rows: avatar + 2 text lines -------------------------- */
export function SkeletonList({ rows = 6, avatar = true, className = '', testid = 'skeleton-list' }) {
  return (
    <div className={`space-y-2 ${className}`} data-testid={testid}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl border border-white/5 bg-card p-3">
          {avatar && <Skeleton className="w-10 h-10 rounded-full shrink-0" />}
          <div className="flex-1 min-w-0 space-y-1.5">
            <Skeleton className="h-3 w-2/3 rounded" />
            <Skeleton className="h-2.5 w-1/3 rounded opacity-80" />
          </div>
          <Skeleton className="h-3 w-10 rounded shrink-0 opacity-70" />
        </div>
      ))}
    </div>
  );
}


/* -------- Card grid: 2×N tiles with photo + title placeholder ------- */
export function SkeletonCardGrid({ tiles = 6, cols = 2, testid = 'skeleton-grid' }) {
  return (
    <div
      className={`grid gap-2`}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      data-testid={testid}
    >
      {Array.from({ length: tiles }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-white/5 bg-card overflow-hidden">
          <Skeleton className="w-full aspect-[3/4]" />
          <div className="p-2 space-y-1.5">
            <Skeleton className="h-2.5 w-3/4 rounded" />
            <Skeleton className="h-2 w-1/2 rounded opacity-70" />
          </div>
        </div>
      ))}
    </div>
  );
}


/* -------- Dashboard tiles: stats + hero card ----------------------- */
export function SkeletonDashboard({ testid = 'skeleton-dashboard' }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-4 w-40 rounded" />
        <Skeleton className="h-6 w-56 rounded" />
      </div>
      {/* 3-stat row */}
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-2xl border border-white/5 bg-black/30 p-3 text-center">
            <Skeleton className="h-6 w-10 mx-auto rounded" />
            <Skeleton className="h-2 w-16 mx-auto mt-2 rounded opacity-70" />
          </div>
        ))}
      </div>
      {/* Hero card */}
      <Skeleton className="h-32 rounded-3xl" />
      {/* Feature rows */}
      <SkeletonList rows={3} avatar />
    </div>
  );
}


/* -------- Battle boss / hero card ---------------------------------- */
export function SkeletonBossHero({ testid = 'skeleton-boss-hero' }) {
  return (
    <div className="rounded-3xl border border-white/10 p-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <Skeleton className="w-16 h-16 rounded-2xl shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-2.5 w-16 rounded" />
          <Skeleton className="h-4 w-3/4 rounded" />
          <Skeleton className="h-2 w-full rounded" />
          <div className="flex justify-between">
            <Skeleton className="h-2 w-20 rounded opacity-70" />
            <Skeleton className="h-2 w-16 rounded opacity-70" />
          </div>
        </div>
      </div>
    </div>
  );
}


/* -------- Announcement / post card -------------------------------- */
export function SkeletonPost({ testid = 'skeleton-post' }) {
  return (
    <div className="rounded-2xl border border-white/5 bg-card p-3 space-y-2" data-testid={testid}>
      <Skeleton className="h-3 w-1/2 rounded" />
      <Skeleton className="h-2 w-1/3 rounded opacity-70" />
      <Skeleton className="h-2 w-full rounded" />
      <Skeleton className="h-2 w-4/5 rounded" />
    </div>
  );
}


/* -------- Screen-level: matches the LiveArena layout while loading */
export function SkeletonArena({ testid = 'skeleton-arena' }) {
  return (
    <div className="p-3 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Skeleton className="w-10 h-10 rounded-full" />
        <Skeleton className="flex-1 h-12 rounded-2xl" />
        <Skeleton className="w-10 h-10 rounded-full" />
      </div>
      <Skeleton className="flex-1 rounded-3xl" style={{ height: '55svh' }} />
      <div className="flex items-end justify-between gap-3">
        <Skeleton className="w-14 h-14 rounded-full" />
        <Skeleton className="w-24 h-24 rounded-full" />
        <Skeleton className="w-14 h-14 rounded-full" />
      </div>
    </div>
  );
}
